import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchMyCampaigns, type MyCampaign } from '../services/campaigns';
import { DailyStatsChart } from '../components/admin/DailyStatsChart';

const CampaignPerformance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [campaigns, setCampaigns] = useState<MyCampaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMyCampaigns();
        if (!cancelled) {
          setCampaigns(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Unable to load your campaigns right now.';
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!id) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-8">
        <p className="text-sm text-red-600">No campaign ID was provided.</p>
        <p className="mt-2 text-sm">
          <Link to="/campaigns" className="text-blue-600 underline">
            Back to My campaigns
          </Link>
        </p>
      </main>
    );
  }

  const campaign = campaigns.find((c) => c.id === id);

  const created = campaign?.createdAt ? new Date(campaign.createdAt) : null;
  const hasPerformanceTotals =
    campaign &&
    ((typeof campaign.views === 'number' && campaign.views > 0) ||
      (typeof campaign.clicks === 'number' && campaign.clicks > 0) ||
      (typeof campaign.socialClicks === 'number' && campaign.socialClicks > 0));

  const ctrPercent =
    campaign &&
    typeof campaign.views === 'number' &&
    campaign.views > 0 &&
    typeof campaign.clicks === 'number'
      ? (campaign.clicks / campaign.views) * 100
      : null;

  return (
    <main className="mx-auto max-w-4xl px-4 py-8 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Campaign performance</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Views, clicks, and safety blanks over time for a single funded campaign.
          </p>
        </div>
        <Link
          to="/campaigns"
          className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
        >
          Back to My campaigns
        </Link>
      </div>

      {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading campaign details…</p>}
      {error && !loading && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && !campaign && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-sm text-slate-700 dark:text-slate-300">
          <p>We couldn&apos;t find that campaign in your account.</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            This can happen if the link is out of date or you&apos;re signed in as a different user.
          </p>
        </div>
      )}

      {!loading && !error && campaign && (
        <div className="space-y-4">
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-sm text-slate-800 dark:text-slate-200 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-1 items-start gap-3">
                {campaign.adThumbnailUrl ? (
                  <img
                    src={campaign.adThumbnailUrl}
                    alt={campaign.adTitle || 'Campaign creative'}
                    className="h-16 w-24 flex-shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <div className="h-16 w-24 flex-shrink-0 rounded-md bg-slate-100 dark:bg-slate-800" />
                )}
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {campaign.packageName || 'Campaign funding event'}
                    </span>
                    <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium capitalize text-slate-700 dark:text-slate-300">
                      {campaign.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    {campaign.adTitle ? campaign.adTitle : 'No creative attached yet'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {created && !Number.isNaN(created.getTime())
                      ? created.toLocaleString()
                      : 'Time not available'}
                    {' · '}Ref ID
                    <span className="ml-1 font-mono text-[11px]">{campaign.id}</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 text-xs text-slate-600 dark:text-slate-400">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {campaign.currency} {campaign.amount.toFixed(2)}
                </span>
                {hasPerformanceTotals ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    {typeof campaign.views === 'number' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5">
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">Total views</span>
                        <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">
                          {campaign.views}
                        </span>
                      </span>
                    )}
                    {typeof campaign.clicks === 'number' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5">
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">Total clicks</span>
                        <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">
                          {campaign.clicks}
                        </span>
                      </span>
                    )}
                    {typeof campaign.socialClicks === 'number' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5">
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">Social clicks</span>
                        <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">
                          {campaign.socialClicks}
                        </span>
                      </span>
                    )}
                    {ctrPercent !== null && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5">
                        <span className="text-[10px] text-emerald-700">CTR</span>
                        <span className="text-[11px] font-semibold text-emerald-800">
                          {ctrPercent.toFixed(1)}%
                        </span>
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">No aggregate performance data yet.</span>
                )}
              </div>
            </div>
          </section>

          {!campaign.adId && (
            <section className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-4 text-xs text-slate-600 dark:text-slate-400">
              <p>This campaign doesn&apos;t have a creative attached yet, so there&apos;s no performance data to show.</p>
              <p className="mt-1">
                Attach or create a creative from the Start a campaign flow to begin collecting analytics.
              </p>
            </section>
          )}

          {campaign.adId && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Daily performance</h2>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                These charts show activity for the ad linked to this campaign. Data may take a few minutes to appear after the first impressions.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                  <DailyStatsChart adId={campaign.adId} metric="views" />
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                  <DailyStatsChart adId={campaign.adId} metric="clicks" />
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                <DailyStatsChart adId={campaign.adId} metric="telemetryBlankEvents" />
              </div>
            </section>
          )}
        </div>
      )}
    </main>
  );
};

export default CampaignPerformance;
